/** Helpers shared between UnifiedProviderCard and ProviderSections. */
import type { ProviderInfo } from '../../types';

export type DataSource = 'crawl' | 'csv' | 'manual' | 'admin';

export interface RoleOption {
  value: string;
  label: string;
  is_clinical?: boolean;
}

export interface SelectOption {
  value: string;
  label: string;
}

/** Fields the crawl typically populates. */
export const CRAWL_FIELDS = new Set([
  'first_name', 'last_name', 'title', 'photo_url', 'website_url',
  'bio', 'specialties', 'license_type', 'degrees', 'role', 'roles',
  'age_range', 'suggested_appointment_types',
]);

/** Check if a value is non-empty (string, array, or truthy). */
export function hasValue(v: unknown): boolean {
  if (v === null || v === undefined || v === '') return false;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

/** Determine which sections auto-expand based on data source and content. */
export function getDefaultExpanded(provider: ProviderInfo, source: DataSource): Set<number> {
  const expanded = new Set<number>();
  if (source === 'admin') {
    for (let i = 0; i < 7; i++) expanded.add(i);
    return expanded;
  }
  expanded.add(0); // always expand identity
  const check = (fields: string[]) =>
    fields.some((f) => hasValue((provider as unknown as Record<string, unknown>)[f]));
  if (check(['roles', 'npi_number', 'taxonomy_code', 'license_type', 'licenses', 'degrees', 'billing_tier'])) expanded.add(1);
  if (check(['bio', 'specialties', 'educational_information', 'age_range'])) expanded.add(2);
  if (check(['suggested_appointment_types', 'goal_sessions_per_week'])) expanded.add(3);
  if (check(['pay_type', 'fee_notes'])) expanded.add(4);
  if (check(['telehealth_url', 'telehealth_mode_override', 'telehealth_instructions'])) expanded.add(5);
  if (check(['address_line1', 'city', 'state'])) expanded.add(6);
  return expanded;
}

export const SECTION_LABELS = [
  'Identity',
  'Clinical & Credentials',
  'Bio & Practice',
  'Scheduling',
  'Billing & Pay',
  'Telehealth',
  'Address',
] as const;
